import { useState, useEffect } from "react";
import { View, Text, TextInput, StyleSheet, TouchableOpacity, Platform, Alert, ActivityIndicator } from "react-native";
import ActionSheetIOS from "../../utils/helpers/ActionSheetIOS";
import { Picker } from "@react-native-picker/picker";
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view";
import * as ImagePicker from "expo-image-picker";
import { getCleanNameFromId } from "../../utils/helpers/helpers";
import { useSelector } from "react-redux";
import { firebaseApp } from "../../firebase/config";
import BackgroundWithImage from "../../components/ScreenImageBackground";
import UploadPhotoBox from '../../components/UploadPhotoBox'
import { doc, collection, getFirestore, updateDoc, setDoc, arrayUnion } from "firebase/firestore";
import { AntDesign } from '@expo/vector-icons';
import { uploadImage } from "../../utils/helpers/functions";
import {
    executeFirestoreWriteWithQueue,
    isDeviceOnline,
} from "../../utils/offlineWriteQueue";
import { queueDeferredMediaUpload } from "../../utils/deferredMediaQueue";

const SingleWindowDefectReportScreen = ({ navigation }) => {
    const [comment, setComment] = useState('')
    const [level, setLevel] = useState('')
    const [selectedWindow, setSelectedWindow] = useState('')

    const [loading, setLoading] = useState(false)
    const [media, setMedia] = useState([])

    const currentClean = useSelector((state) => state.buildingList.currentClean);
    const currentDrop = useSelector((state) => state.buildingList.currentDrop);
    const buildingName = useSelector((state) => state.buildingList.buildingName);

    const userUid = useSelector((state) => state.auth.uid);
    const date = new Date();

    const db = getFirestore(firebaseApp);
    const buildingRef = doc(db, "BuildingsX", buildingName?.site_id);

    const dropRef = doc(
        buildingRef,
        "currentYear",
        currentClean,
        "drops",
        currentDrop.name
    );

    const defectsRef = collection(buildingRef, 'defects')

    const isMounted = { current: true };

    useEffect(() => {
        isMounted.current = true;
        (async () => {
            if (Platform.OS !== "web") {
                const { status } =
                    await ImagePicker.requestMediaLibraryPermissionsAsync();
                if (status !== "granted") {
                    alert("Sorry, we need camera roll permissions to make this work!");
                }
            }
        })();
        return () => {
            isMounted.current = false;
        };
    }, []);

    const pickImage = async () => {
        try {
            if (media.length >= 5) {
                Alert.alert('Media limit', 'You can only upload up to 5 images.');
                return;
            }
            const result = await ImagePicker.launchImageLibraryAsync({
                mediaTypes: ImagePicker.MediaTypeOptions.Images,
                allowsMultipleSelection: true,
                quality: 1,
                selectionLimit: 5 - media.length,
            });
            // Modern shape: result.assets; legacy: result.uri or result.cancelled
            const assets = result?.assets || (result?.uri ? [{ uri: result.uri }] : []);
            const uris = assets.map(a => a.uri).filter(Boolean);
            if (uris.length) {
                setMedia(prev => [...prev, ...uris].slice(0, 5));
            }
        } catch (err) {
            console.log('Image pick error', err);
        }
    };

    const submitHandler = async () => {
        setLoading(true)
        try {
            const isOnline = await isDeviceOnline();
            const shouldQueueMediaUpload = !isOnline && Boolean(image)

            // Validate presence of currentDrop safely
            if (!currentDrop) throw new Error('No current drop selected.')

            if ((currentDrop.singleWindows?.length || 0) > 0 && selectedWindow === '') {
                throw new Error('Please select a window.')
            }

            if (!media.length || !level || !comment) {
                throw new Error('Please make sure to fill every field.')
            }

            const dropNamePath = `${currentDrop.name}_Level_${level}`
            const singleWindowPath = `${dropNamePath}_${selectedWindow}`

            const uploaded = shouldQueueMediaUpload ? [] : await uploadImage(media, dropNamePath)
            const imagesArr = Array.isArray(uploaded) ? uploaded : [uploaded]

            const docToSave = {
                name: (currentDrop.singleWindows?.length || 0) ? singleWindowPath : dropNamePath,
                dropId: currentDrop.name,
                fixDate: null,
                defectReportDate: date,
                defectType: 'building',
                defectTypeLabel: 'building',
                inService: "",
                summary: comment.trim(),
                priority: 'medium',
                status: 'open',
                isResolved: false,
                completed: false,
                source: 'SingleWindowDefectReportScreen',
                images: imagesArr,
                equipmentId: "",
                completedBy: userUid,
                cleanId: currentClean
            }

            const defectDocRef = doc(defectsRef)
            const queuedMediaUploadId = shouldQueueMediaUpload
                ? await queueDeferredMediaUpload({
                    label: "single-window-defect",
                    mediaUris: media,
                    storageBasePath: `single-window-defects/${buildingName?.site_id}/${currentDrop.name}`,
                    documentPaths: [defectDocRef.path],
                })
                : null

            const { queued } = await executeFirestoreWriteWithQueue({
                db,
                queueLabel: "single-window-defect",
                writeOperation: async () => {
                    await setDoc(defectDocRef, {
                        ...docToSave,
                        images: shouldQueueMediaUpload ? [] : imagesArr,
                        mediaUploadStatus: shouldQueueMediaUpload ? "queued" : "uploaded",
                        pendingMediaCount: shouldQueueMediaUpload ? 1 : 0,
                        queuedMediaUploadId: queuedMediaUploadId || null,
                    })

                    // Only update dropRef if we have a valid dropRef and currentDrop
                    if (dropRef && currentDrop?.name) {
                        await updateDoc(dropRef, {
                            isFaulty: true,
                            cleanStatus: 'Faulty',
                            faultyWindows: arrayUnion(defectDocRef.id)
                        })
                    }
                },
            })

            if (isMounted.current) {
                setLoading(false)
                setMedia([])
            }
            submitted(queued, shouldQueueMediaUpload)

        } catch (error) {
            console.log(error)
            const message = error?.message || 'Unable to submit defect.'
            if (Platform.OS === 'web') alert(message)
            else Alert.alert('Oops', message)
            if (isMounted.current) setLoading(false)
        }
    }

    const submitted = (queued = false, photosQueued = false) => {
        const alertMessage = queued
            ? photosQueued
                ? `${currentDrop.name} saved offline. Photos queued for upload.`
                : `${currentDrop.name} saved offline and queued for sync.`
            : `${currentDrop.name} just completed. Thank you`

        if (Platform.OS === 'web') {
            alert(alertMessage)
            navigation.goBack()
        } else {
            Alert.alert('Done', alertMessage, [
                {
                    text: 'OK',
                    onPress: () => navigation.goBack()
                }
            ])
        }
    }

    const openFormMenuOnIos = () => {
      if (!ActionSheetIOS) {
        return Alert.alert(
          "Unsupported",
          "Action sheets are only supported on iOS in this web build."
        );
      }

      ActionSheetIOS.showActionSheetWithOptions(
        {
          options: ["Cancel", "L", "R"],
          cancelButtonIndex: 0,
          userInterfaceStyle: "dark",
        },
        buttonIndex => {
          if (buttonIndex === 0) {
            return setSelectedWindow("");
          }
          setSelectedWindow("L"); // @todo - Will return to this once web is working.
        }
      );
    };

    return (
        <KeyboardAwareScrollView
            contentContainerStyle={{ flex: 1 }}
            enableOnAndroid={true}
            automaticallyAdjustContentInsets={true}
            enableAutomaticScroll={(Platform.OS === 'ios')}
        >

            <BackgroundWithImage>
                <View style={styles.contentContainer}>
                    <TouchableOpacity style={{ alignSelf: 'flex-start' }} onPress={() => navigation.goBack()}>
                        <AntDesign name="close" size={30} color="white" />
                    </TouchableOpacity>

                    <Text style={styles.infoText}>Clean: {getCleanNameFromId(currentClean)}</Text>
                    <Text style={styles.infoText}>Drop: {currentDrop.name}</Text>

                    <View style={styles.reportingBox}>

                        <TextInput
                            value={level}
                            placeholder="Level"
                            numberOfLines={1}
                            multiline
                            textAlignVertical="top"
                            returnKeyType="done"
                            onChangeText={(text) => setLevel(text)}
                            style={{ ...styles.commentsInput, height: 40, overflow: 'hidden' }}
                        />

                        {Platform.OS === 'ios' ?
                            <TouchableOpacity onPress={openFormMenuOnIos} style={styles.iosButton}>
                                <Text style={styles.iosButtonText}>{selectedWindow ? selectedWindow : 'Please select'}</Text>
                            </TouchableOpacity>

                            :
                            !!currentDrop.singleWindows.length && (
                                <Picker
                                    style={styles.picker}
                                    selectedValue={selectedWindow}
                                    onValueChange={(itemValue, itemIndex) => {
                                        setSelectedWindow(itemValue);
                                    }}
                                >
                                    <Picker.Item label="Please select" value="" />
                                    {currentDrop.singleWindows.map((d) => {
                                        return <Picker.Item key={d} label={d} value={d} />;
                                    })}
                                </Picker>
                            )
                        }

                        <TextInput
                            value={comment}
                            placeholder="Enter a comment..."
                            numberOfLines={12}
                            multiline
                            textAlignVertical="top"
                            returnKeyType="done"
                            onChangeText={(text) => setComment(text)}
                            style={styles.commentsInput}
                        />

                        <UploadPhotoBox pickMedia={pickImage} media={media} />
                    </View>

                    <TouchableOpacity style={styles.submitButtonBox} onPress={submitHandler}>
                        {loading ? <ActivityIndicator /> : <Text style={styles.submitButtonText}>Submit</Text>}
                    </TouchableOpacity>
                </View>
            </BackgroundWithImage>

        </KeyboardAwareScrollView>
    )
}

const styles = StyleSheet.create({
    container: {
        flex: 1
    },
    contentContainer: {
        marginHorizontal: 30,
        marginVertical: 50,
        textAlign: 'center'
    },
    almostThereText: {
        color: '#fff',
        fontSize: 40,
        fontWeight: '700',
        marginVertical: 20
    },
    infoText: {
        color: '#fff',
        fontSize: 20
    },
    reportingBox: {
        marginVertical: 50,
        height: 350
    },
    commentsInput: {
        backgroundColor: '#fff',
        marginVertical: 10,
        padding: 10,
        borderRadius: 10,
        height: 240
    },
    submitButtonBox: {
        alignItems: 'center'
    },
    submitButtonText: {
        color: '#fff',
        fontSize: 30,
        fontWeight: '700'
    },
    imagePickerContainer: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: 'white',
        zIndex: 9,
    },
    picker: {
        // width: 150,
        height: 50,
        borderRadius: 10,
        marginTop: 5,
        color: "black",
        backgroundColor: "#F6F6F6",
    },
    iosButton: {
        width: 200,
        height: 50,
        borderRadius: 10,
        marginTop: 5,
        backgroundColor: "#F6F6F6",
        justifyContent: 'center'
    },
    iosButtonText: {
        color: 'black',
        paddingHorizontal: 10
    }
})

export default SingleWindowDefectReportScreen